import React, { useEffect, useState, useCallback } from 'react';
import { TrendingUp, ChevronRight, Loader2 } from 'lucide-react';
import { surveyAPI } from '../../services/api';
import { toast } from 'sonner';

/**
 * Shows the most-active OPEN survey/poll in the community.
 * - For single-question polls: residents can vote inline by tapping an option (with a confirm dialog).
 * - For multi-question surveys (or after voting): shows leading option + tap-to-open full modal.
 */
export function TrendingSurveyCard({ onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: list } = await surveyAPI.listForResident();
      // Prefer surveys with votes; fall back to most recent open one (for inline-vote prompt even at 0 votes)
      const active = (list || []).filter(s => s.status === 'active');
      if (active.length === 0) { setData(null); return; }
      active.sort((a, b) => (b.response_count || 0) - (a.response_count || 0));
      const top = active[0];
      const { data: agg } = await surveyAPI.aggregated(top.id);
      const q0 = agg.aggregated[0];
      const total = q0.counts.reduce((a, b) => a + b, 0) || 1;
      let leadIdx = 0;
      for (let i = 1; i < q0.counts.length; i += 1) {
        if (q0.counts[i] > q0.counts[leadIdx]) leadIdx = i;
      }
      setData({
        survey_id: top.id,
        title: top.title,
        type: top.type,
        is_poll: top.questions.length === 1,
        question: q0.question,
        options: q0.options,
        counts: q0.counts,
        total,
        lead_idx: leadIdx,
        responded: top.responded,
        my_answer: agg.my_answers ? agg.my_answers[0] : null,
        response_count: agg.response_count,
      });
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const quickVote = async (e, optIdx) => {
    e.stopPropagation();
    if (!data || data.responded || voting) return;
    if (!window.confirm(`Vote "${data.options[optIdx]}"? Your response cannot be changed.`)) return;
    setVoting(true);
    try {
      await surveyAPI.respond(data.survey_id, [optIdx]);
      toast.success('Vote recorded · live results below');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const showInlineVote = data.is_poll && !data.responded;

  return (
    <div data-testid="trending-survey-card" className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md p-3 sm:p-4">
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-start gap-3 text-left group"
        data-testid="trending-survey-open-btn"
      >
        <div className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
          <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] sm:text-[11px] uppercase tracking-wide font-bold text-indigo-200">Trending now</span>
            {data.responded && <span className="text-[9px] bg-emerald-400/30 text-emerald-100 px-1.5 py-0.5 rounded-full font-semibold">You voted</span>}
            {!data.is_poll && <span className="text-[9px] bg-amber-400/30 text-amber-100 px-1.5 py-0.5 rounded-full font-semibold">{data.title.length > 0 ? `${data.options.length}+ questions` : 'Survey'}</span>}
          </div>
          <p data-testid="trending-survey-title" className="text-sm sm:text-base font-bold text-white leading-tight truncate">{data.title}</p>
          <p className="text-[11px] sm:text-xs text-indigo-100 truncate mt-0.5">{data.question}</p>
        </div>
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-white/70 group-hover:translate-x-1 transition-transform self-center shrink-0" />
      </button>

      {showInlineVote ? (
        <div data-testid="trending-quick-vote" className="mt-2.5 space-y-1.5">
          <p className="text-[10px] text-indigo-100/90 italic">⚡ Quick vote — tap to cast directly from here</p>
          {data.options.map((opt, idx) => (
            <button
              key={idx}
              data-testid={`trending-quick-vote-opt-${idx}`}
              type="button"
              disabled={voting}
              onClick={(e) => quickVote(e, idx)}
              className="w-full flex items-center justify-between gap-2 bg-white/10 hover:bg-white/20 active:bg-white/30 disabled:opacity-50 backdrop-blur rounded-lg px-2.5 py-1.5 transition-colors group/opt"
            >
              <span className="text-xs sm:text-sm font-semibold text-white truncate text-left">{opt}</span>
              {voting ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white shrink-0" /> : (
                <span className="text-[10px] bg-white/15 text-white px-2 py-0.5 rounded-full font-semibold shrink-0 group-hover/opt:bg-white/25">Tap to vote</span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-2.5 bg-white/10 backdrop-blur rounded-lg px-2.5 py-1.5 flex items-center justify-between gap-2">
          <span data-testid="trending-survey-lead" className="text-xs sm:text-sm font-semibold text-white truncate">
            {data.options[data.lead_idx]}
            {data.responded && data.my_answer === data.lead_idx && <span className="text-[10px] text-indigo-100 font-normal ml-1">· your vote</span>}
          </span>
          <div className="flex items-baseline gap-2 shrink-0">
            <span className="text-base sm:text-lg font-extrabold text-white">{Math.round((data.counts[data.lead_idx] / data.total) * 100)}%</span>
            <span className="text-[10px] text-indigo-100">· {data.response_count} {data.response_count === 1 ? 'vote' : 'votes'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrendingSurveyCard;

